import { useFormData, useFormInstance } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { PhoneNumberInput } from '@neo/primitives/phone-number-input'
import type { ApiResponse } from '@shared/types'
import type { AxiosError } from 'axios'
import dayjs from 'dayjs'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Navigate, useLocation, useNavigate } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import type { TwoFactorAuthResponse } from 'v2/types/api/auth'
import { errorToast } from 'v2/utils/toasts'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import { AUTH_PRIMARY_BUTTON_CLASS } from './components/auth-styles'
import { useTwoFactorAuthMutation } from './queries'

type TwoFactorAuthMutationResponse = ApiResponse<TwoFactorAuthResponse>

interface TwoFactorAuthFormData {
  phone_number: string
}

const PhoneNumberInputAdapter = ({
  value,
  onChange,
  inputProps,
  disabled,
  className,
  label,
}: CompProps<TwoFactorAuthFormData, string>) => (
  <PhoneNumberInput
    value={value}
    onChange={onChange}
    placeholder={(inputProps as { placeholder?: string })?.placeholder || ''}
    disabled={disabled}
    className={className}
    label={label}
  />
)

const validatePhoneNumber = (value: string, allowIncomplete = false): boolean => {
  if (!value) return false

  const trimmedValue = value.trim()

  // If it's just a country code (like "+91" or "+91 "), handle based on allowIncomplete flag
  if (/^\+\d{1,4}\s*$/.test(trimmedValue)) {
    return allowIncomplete
  }

  // Parse the phone number using libphonenumber-js
  const phoneNumber = parsePhoneNumberFromString(trimmedValue)

  // Return true only if parsing succeeds and number is valid
  return phoneNumber?.isValid() || false
}

const isCompleteValidPhoneNumber = (value: string): boolean => validatePhoneNumber(value, false)

const normalizePhoneNumber = (phoneNumberString: string): string => {
  const phoneNumber = parsePhoneNumberFromString(phoneNumberString)
  return phoneNumber?.format('E.164') || phoneNumberString
}

const fields: GenericFieldConfig<TwoFactorAuthFormData> = [
  {
    name: 'phone_number',
    type: 'custom',
    component: PhoneNumberInputAdapter,
    zodValidation: z
      .string()
      .min(1, 'Phone number is required')
      .refine((value) => validatePhoneNumber(value, true), 'Please enter a valid phone number'),
    label: 'Phone number',
    inputProps: {
      placeholder: 'Enter phone number',
      autoComplete: 'tel',
      'data-testid': 'input-phone-number',
    },
    eventDataExtractor: (e) => (typeof e === 'string' ? e : e?.target?.value),
  },
]

export const TwoFactorAuth = () => {
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const navigate = useNavigate()
  const location = useLocation()
  const formData = useFormData<TwoFactorAuthFormData>('two-factor-auth-form')
  const form = useFormInstance<TwoFactorAuthFormData>('two-factor-auth-form')
  const currentPhoneNumber = formData?.phone_number || ''
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false)

  const {
    aomUUID,
    orgID,
    accountID,
    email,
    password,
    isSignup,
    isSso,
    isInvite,
    verificationPending,
    originalLoginResponse,
    region,
  } = location.state || {}

  const defaultValueRef = useRef<TwoFactorAuthFormData>({
    phone_number: '',
  })

  const twoFactorAuthMutation = useTwoFactorAuthMutation(recaptchaRef)

  // Track phone number validation state in real-time
  useEffect(() => {
    if (!form) return

    const unsubscribe = form.store.subscribe(() => {
      const phoneNumberField = form.getFieldInfo('phone_number')
      const currentValue = phoneNumberField.instance?.state.value || ''
      const hasErrors = (phoneNumberField.instance?.state.meta.errors?.length || 0) > 0

      // Check if we have a complete valid phone number (not just country code)
      const isValid = !hasErrors && isCompleteValidPhoneNumber(currentValue)
      setIsPhoneNumberValid(isValid)
    })

    return () => unsubscribe()
  }, [form])

  // Redirect if required data is missing
  if (!aomUUID || !email || (!isSignup && !isSso && !isInvite && !password)) {
    errorToast({
      title: 'Session Expired',
      description: 'Required authentication data is missing. Please start the process again.',
    })
    return <Navigate to={isSignup ? '/signup' : '/login'} replace />
  }

  // Helper function to navigate to verification page after successful OTP request
  const navigateToVerification = (
    normalizedPhoneNumber: string,
    response: TwoFactorAuthMutationResponse,
    method: 'sms' | 'voice',
  ) => {
    const otpSentTime = dayjs().toISOString()
    navigate('/two-factor-verify', {
      state: {
        phoneNumber: normalizedPhoneNumber,
        sessionUuid: response.data.session_uuid,
        aomUUID,
        accountID: response.data.account_id,
        method,
        email,
        password,
        orgID: orgID || accountID, // Pass the ID we used
        isSignup, // true if orgID exists (signup flow), false if accountID exists (login flow)
        isSso,
        isInvite,
        verificationPending,
        originalLoginResponse,
        region,
        otpSentTime,
      },
    })
  }

  const handleSubmit = async (value: TwoFactorAuthFormData) => {
    try {
      const normalizedPhoneNumber = normalizePhoneNumber(value.phone_number)

      const response = await twoFactorAuthMutation.mutateAsync({
        phone_number: normalizedPhoneNumber,
        method: 'sms',
        org_id: Number(orgID || accountID), // Use accountID if orgID is not available (login flow)
        aom_uuid: aomUUID,
        is_signup: isSignup,
        region,
        email,
      })

      navigateToVerification(normalizedPhoneNumber, response, 'sms')
    } catch (_error) {
      // Error is already handled by the mutation's onError callback
    }
  }

  const handleSendViaCall = async () => {
    if (!currentPhoneNumber) {
      return
    }

    try {
      const normalizedPhoneNumber = normalizePhoneNumber(currentPhoneNumber)

      const response = await twoFactorAuthMutation.mutateAsync({
        phone_number: normalizedPhoneNumber,
        method: 'voice',
        aom_uuid: aomUUID,
        org_id: Number(orgID || accountID), // Use accountID if orgID is not available (login flow)
        is_signup: isSignup,
        region,
        email,
      })

      navigateToVerification(normalizedPhoneNumber, response, 'voice')
    } catch (_error) {
      // Error is already handled by the mutation's onError callback
    }
  }

  // Both buttons should be disabled until phone number is fully valid
  const areButtonsDisabled = twoFactorAuthMutation.isPending || !isPhoneNumberValid
  const isSmsPending =
    twoFactorAuthMutation.isPending && twoFactorAuthMutation.variables?.method === 'sms'
  const isCallPending =
    twoFactorAuthMutation.isPending && twoFactorAuthMutation.variables?.method === 'voice'

  return (
    <AuthLayout pageTitle="Two factor verification" title="" description="">
      <div className="w-[420px] mr-0 -ml-10 flex flex-col space-y-6">
        {twoFactorAuthMutation.error && (
          <Alert
            variant="destructive"
            content={getErrorText(twoFactorAuthMutation.error as AxiosError<ApiResponse<unknown>>)}
          />
        )}
        <div>
          <h1 className="text-h1-500">Secure your account by adding Two-Factor Authentication</h1>
          <p className="text-p-400 text-secondary mt-2">
            A one-time password (OTP) will be sent to your phone number via SMS and this number will
            be used for verification each time you log in
          </p>
        </div>
        <GenericForm
          id="two-factor-auth-form"
          defaultValues={defaultValueRef.current}
          fields={fields}
          onSubmit={handleSubmit}
          className="grid gap-4"
          isSubmitting={isSmsPending}
          submitText="Send code via sms"
          includeSubmitButton={false}
        />
        <FormSubmitButton
          formId="two-factor-auth-form"
          typography="action"
          className={AUTH_PRIMARY_BUTTON_CLASS}
          size="xl"
          loading={isSmsPending}
          disabled={areButtonsDisabled}
          disabledReason={!isPhoneNumberValid ? 'Please enter a valid phone number' : undefined}
          data-testid="button-send-sms"
        >
          Request via SMS
        </FormSubmitButton>
        <div className="text-center">
          <button
            type="button"
            className="text-primary text-s-500 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-send-call"
            onClick={handleSendViaCall}
            disabled={areButtonsDisabled}
            title={!isPhoneNumberValid ? 'Please enter a valid phone number' : ''}
          >
            {isCallPending ? 'Sending...' : 'Request via a Call'}
          </button>
        </div>
      </div>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
